import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Trash2, Users, Edit3, Sparkles, Save } from "lucide-react";
import { ROLE_LABELS, useAuth } from "../lib/auth";
import { toast } from "sonner";

const Creators = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const canManage = user?.role === "ceo" || user?.role === "director_marketing";

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/users"), api.get("/users/role-templates")])
      .then(([u, t]) => { setUsers(u.data); setTemplates(t.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (u) => {
    if (!window.confirm(`¿Desactivar a ${u.name}? Podrás reactivarlo después.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario desactivado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const activeUsers = users.filter((u) => u.active !== false);
  const grouped = activeUsers.reduce((acc, u) => {
    acc[u.role] = acc[u.role] || [];
    acc[u.role].push(u);
    return acc;
  }, {});

  return (
    <Layout
      title="Creadores & Roles"
      subtitle="Cada perfil enriquecido permite a Aura personalizar guiones y briefs"
      actions={canManage && (
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-creator-btn">
          <Plus size={14} /> Nuevo miembro
        </button>
      )}
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([role, list]) => (
            <section key={role}>
              <div className="label-overline mb-3">{ROLE_LABELS[role] || role} <span className="text-[#666]">({list.length})</span></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((u) => {
                  const profileCompleted = u.creator_profile && Object.keys(u.creator_profile).length > 0;
                  return (
                    <div key={u.id} className="aura-card p-5 group relative" data-testid={`user-${u.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#D99D26]/15 flex items-center justify-center text-[#8a5e10] font-display font-semibold text-lg overflow-hidden flex-shrink-0">
                          {u.avatar_url ? <img src={u.avatar_url} alt={u.name} className="w-full h-full object-cover" /> : (u.name?.charAt(0) || "?").toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-medium text-base">{u.name}</div>
                          <div className="text-xs text-[#666] mt-0.5 truncate">{u.email}</div>
                          {u.bio && <div className="text-xs text-[#666] mt-1 line-clamp-2">{u.bio}</div>}
                          <div className="flex flex-wrap gap-1 mt-2">
                            {u.branch && <span className="pill pill-gray text-[10px]">{u.branch}</span>}
                            {profileCompleted ? (
                              <span className="pill pill-green text-[10px] flex items-center gap-0.5"><Sparkles size={9} /> Perfil IA listo</span>
                            ) : (
                              <span className="pill pill-yellow text-[10px]">Perfil IA pendiente</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {canManage && (
                        <div className="flex justify-end gap-1 mt-3 pt-3 border-t border-[#F0F0F0] opacity-70 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => setEditing(u)} className="text-xs text-[#8a5e10] font-medium hover:text-[#0A0A0A] flex items-center gap-1" data-testid={`edit-${u.id}`}>
                            <Edit3 size={11} /> Editar perfil
                          </button>
                          <button onClick={() => remove(u)} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 ml-3" data-testid={`delete-${u.id}`}>
                            <Trash2 size={11} /> Desactivar
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {showForm && <CreatorForm templates={templates} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {editing && <CreatorEditModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </Layout>
  );
};

const CreatorForm = ({ templates, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "creador_interno", role_template: "",
    bio: "", branch: "", phone: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || form.password.length < 6) {
      toast.error("Nombre, correo y contraseña mínima 6 chars requeridos");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Usuario creado · ahora edita su perfil para que Aura lo conozca");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-lg p-7 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start">
          <div><div className="label-overline">Equipo</div><h2 className="font-display text-2xl font-medium">Nuevo miembro</h2></div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="aura-label">Nombre *</label><input required className="aura-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
          <div><label className="aura-label">Email *</label><input required type="email" className="aura-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
          <div><label className="aura-label">Contraseña inicial *</label><input required type="password" className="aura-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
          <div>
            <label className="aura-label">Rol funcional</label>
            <select className="aura-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="aura-label">Plantilla</label>
            <select className="aura-input" value={form.role_template} onChange={(e) => setForm({ ...form, role_template: e.target.value })}>
              <option value="">— Sin plantilla —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div><label className="aura-label">Sucursal</label><input className="aura-input" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} placeholder="Tampico..." /></div>
          <div><label className="aura-label">Teléfono</label><input className="aura-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="col-span-2"><label className="aura-label">Bio</label><textarea className="aura-input" rows="2" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>
        </div>
        <button type="submit" disabled={saving} className="btn-primary w-full justify-center flex items-center gap-2" data-testid="user-submit">
          {saving && <span className="spinner" />} Crear usuario
        </button>
      </form>
    </div>
  );
};

const arrToText = (arr) => Array.isArray(arr) ? arr.join("\n") : "";
const textToArr = (txt) => (txt || "").split("\n").map((s) => s.trim()).filter(Boolean);

const CreatorEditModal = ({ user, onClose, onSaved }) => {
  const cp = user.creator_profile || {};
  const [form, setForm] = useState({
    name: user.name || "",
    bio: user.bio || "",
    branch: user.branch || "",
    role: user.role,
    creator_profile: {
      style: cp.style || "",
      voice_tone: cp.voice_tone || "",
      niche: cp.niche || "",
      demographics: cp.demographics || "",
      audience_size: cp.audience_size || "",
      visual_style: cp.visual_style || "",
      on_camera: cp.on_camera !== false,
      strengths: arrToText(cp.strengths),
      weaknesses: arrToText(cp.weaknesses),
      top_topics: arrToText(cp.top_topics),
      no_go_topics: arrToText(cp.no_go_topics),
      signature_phrases: arrToText(cp.signature_phrases),
      examples_viral: arrToText(cp.examples_viral),
      signature_hooks: arrToText(cp.signature_hooks),
      references: arrToText(cp.references),
      languages: arrToText(cp.languages),
    },
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        bio: form.bio,
        branch: form.branch,
        role: form.role,
        creator_profile: {
          style: form.creator_profile.style,
          voice_tone: form.creator_profile.voice_tone,
          niche: form.creator_profile.niche,
          demographics: form.creator_profile.demographics,
          audience_size: form.creator_profile.audience_size,
          visual_style: form.creator_profile.visual_style,
          on_camera: form.creator_profile.on_camera,
          strengths: textToArr(form.creator_profile.strengths),
          weaknesses: textToArr(form.creator_profile.weaknesses),
          top_topics: textToArr(form.creator_profile.top_topics),
          no_go_topics: textToArr(form.creator_profile.no_go_topics),
          signature_phrases: textToArr(form.creator_profile.signature_phrases),
          examples_viral: textToArr(form.creator_profile.examples_viral),
          signature_hooks: textToArr(form.creator_profile.signature_hooks),
          references: textToArr(form.creator_profile.references),
          languages: textToArr(form.creator_profile.languages),
        },
      };
      await api.patch(`/users/${user.id}`, payload);
      toast.success("Perfil actualizado · Aura ya conoce mejor a este creador");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const setCP = (key, val) => setForm((f) => ({ ...f, creator_profile: { ...f.creator_profile, [key]: val } }));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-4xl p-7 space-y-4 max-h-[92vh] overflow-y-auto" data-testid="edit-creator-modal">
        <div className="flex justify-between items-start sticky top-0 bg-white pb-3 -mt-2">
          <div>
            <div className="label-overline">Editar perfil · Aura usará esto para personalizar guiones</div>
            <h2 className="font-display text-2xl font-medium">{user.name}</h2>
            <p className="text-xs text-[#666] mt-0.5">{user.email} · {ROLE_LABELS[user.role] || user.role}</p>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        {/* Datos básicos */}
        <div className="aura-card-flat p-4">
          <div className="label-overline mb-3">📌 Datos básicos</div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="aura-label">Nombre</label><input className="aura-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="aura-label">Sucursal</label><input className="aura-input" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} /></div>
            <div className="col-span-2"><label className="aura-label">Bio</label><textarea className="aura-input" rows="2" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></div>
            <div>
              <label className="aura-label">Rol</label>
              <select className="aura-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Perfil para IA */}
        <div className="aura-card-flat p-4 bg-gradient-to-br from-[#FFF9E6] to-white border border-[#D99D26]/20">
          <div className="label-overline mb-1">🧠 Perfil de creador para Aura</div>
          <p className="text-xs text-[#666] mb-4">Mientras más rico el perfil, mejor genera Aura los guiones para este creador. Una línea por entrada en las listas.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="aura-label">Estilo general</label><input className="aura-input" value={form.creator_profile.style} onChange={(e) => setCP("style", e.target.value)} placeholder="Cinematográfico / casual / comedia trends" data-testid="cp-style" /></div>
            <div><label className="aura-label">Voz y tono</label><input className="aura-input" value={form.creator_profile.voice_tone} onChange={(e) => setCP("voice_tone", e.target.value)} placeholder="Cálida, cercana, regional, sarcástica..." data-testid="cp-voice" /></div>
            <div><label className="aura-label">Nicho</label><input className="aura-input" value={form.creator_profile.niche} onChange={(e) => setCP("niche", e.target.value)} placeholder="Perfumería para hombres jóvenes" /></div>
            <div><label className="aura-label">Demografía audiencia</label><input className="aura-input" value={form.creator_profile.demographics} onChange={(e) => setCP("demographics", e.target.value)} placeholder="Mujeres 22-34 Tamaulipas urbanas" /></div>
            <div><label className="aura-label">Tamaño audiencia</label><input className="aura-input" value={form.creator_profile.audience_size} onChange={(e) => setCP("audience_size", e.target.value)} placeholder="12K IG / 4K TikTok" /></div>
            <div><label className="aura-label">Estilo visual</label><input className="aura-input" value={form.creator_profile.visual_style} onChange={(e) => setCP("visual_style", e.target.value)} placeholder="Aesthetic, raw, IG clean..." /></div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="on-cam" checked={form.creator_profile.on_camera} onChange={(e) => setCP("on_camera", e.target.checked)} className="accent-[#D99D26]" />
              <label htmlFor="on-cam" className="text-sm">Sale en cámara (vs solo voice-over o b-roll)</label>
            </div>

            <div>
              <label className="aura-label">Fortalezas (1 por línea)</label>
              <textarea className="aura-input text-xs" rows="3" value={form.creator_profile.strengths} onChange={(e) => setCP("strengths", e.target.value)} placeholder="Storytelling personal&#10;Comedia rápida&#10;Lectura de cliente" data-testid="cp-strengths" />
            </div>
            <div>
              <label className="aura-label">Debilidades / evitar</label>
              <textarea className="aura-input text-xs" rows="3" value={form.creator_profile.weaknesses} onChange={(e) => setCP("weaknesses", e.target.value)} placeholder="No es técnica&#10;Mal con sarcasmo&#10;No baila" />
            </div>
            <div>
              <label className="aura-label">Temas estrella (top topics)</label>
              <textarea className="aura-input text-xs" rows="3" value={form.creator_profile.top_topics} onChange={(e) => setCP("top_topics", e.target.value)} placeholder="Perfumes árabes&#10;Recomendaciones para citas&#10;Inspiraciones de lujo" />
            </div>
            <div>
              <label className="aura-label">No-go topics (NO tocar)</label>
              <textarea className="aura-input text-xs" rows="3" value={form.creator_profile.no_go_topics} onChange={(e) => setCP("no_go_topics", e.target.value)} placeholder="Política&#10;Religión&#10;Crítica directa a competencia" data-testid="cp-nogo" />
            </div>
            <div>
              <label className="aura-label">Frases firma</label>
              <textarea className="aura-input text-xs" rows="3" value={form.creator_profile.signature_phrases} onChange={(e) => setCP("signature_phrases", e.target.value)} placeholder="'Mi amor, te lo juro...'&#10;'No te puedes ir sin esto'" />
            </div>
            <div>
              <label className="aura-label">Hooks que le funcionan</label>
              <textarea className="aura-input text-xs" rows="3" value={form.creator_profile.signature_hooks} onChange={(e) => setCP("signature_hooks", e.target.value)} placeholder="'Si tu novio huele así...'&#10;Comparativa 4x3 vs 2x1" />
            </div>
            <div>
              <label className="aura-label">Ejemplos virales pasados</label>
              <textarea className="aura-input text-xs" rows="3" value={form.creator_profile.examples_viral} onChange={(e) => setCP("examples_viral", e.target.value)} placeholder="Reel feromonas 200K views&#10;Story 4x3 800 mensajes" />
            </div>
            <div>
              <label className="aura-label">Referencias / inspiraciones</label>
              <textarea className="aura-input text-xs" rows="3" value={form.creator_profile.references} onChange={(e) => setCP("references", e.target.value)} placeholder="@perfumero_oficial&#10;@scentdaily" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-creator-profile">
            {saving ? <span className="spinner" /> : <Save size={14} />} Guardar perfil
          </button>
        </div>
      </form>
    </div>
  );
};

export default Creators;
